import * as React from "react"

export const Video = React.forwardRef<HTMLVideoElement, VideoProps>(
  ({ loading, ...props }, ref) => {
    const lazy = loading === "lazy"
    const videoRef = React.useRef<HTMLVideoElement | null>(null)

    React.useImperativeHandle(ref, () => videoRef.current!, [])

    React.useEffect(() => {
      const video = videoRef.current

      if (video && lazy) {
        const handleIntersection = ([{ isIntersecting }]: IntersectionObserverEntry[]) => {
          if (isIntersecting) {
            video.play()
          } else {
            video.pause()
          }
        }

        const observerOptions: IntersectionObserverInit = {
          root: null,
          rootMargin: "120px 0px",
          threshold: 0,
        }

        const observer = new IntersectionObserver(handleIntersection, observerOptions)

        observer.observe(video)

        return () => {
          observer.disconnect()
        }
      }
    }, [lazy])

    return (
      <video
        ref={videoRef}
        preload={lazy ? "none" : undefined}
        autoPlay={!lazy}
        loop
        muted
        playsInline
        disablePictureInPicture
        {...props}
      />
    )
  },
)

export type VideoProps = JSX.IntrinsicElements["video"] & {
  loading?: "eager" | "lazy"
}
